import React from "react";
//#region types
import { CompanyListProps } from "models/propTypes/companyListPropTypes";
import { TranslationKeys } from "translations";
import { AllCompaniesConstants, IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { SelectChangeEvent, Skeleton } from "@mui/material";
import { Divider, Input, MenuItem, Select, FormControl } from "@mui/material";
import { Icon } from "components/common";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const ChevronDownIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Icon icon={IconDefinitionConstants.WChevronDown} className={className} />
);

const CompanyList: React.FC<CompanyListProps> = (props) => {
  const [open, setOpen] = React.useState<boolean>(false);
  // MUI hands focus back to the trigger on close, which Chrome shows as keyboard focus even after a mouse close.
  const [closedWithPointer, setClosedWithPointer] = React.useState<boolean>(false);

  const onChangeHandler = (event: SelectChangeEvent<string>) => {
    props.onChange(event);
    setOpen(false);
  };

  return (
    <FormControl
      className={clsx(cssExports.companyListFormControl, closedWithPointer && cssExports.companySelectPointerClosed)}
      onBlur={(event) =>
        event.currentTarget.contains(event.target) &&
        !event.currentTarget.contains(event.relatedTarget as Node) &&
        setClosedWithPointer(false)
      }
      onKeyDown={() => setClosedWithPointer(false)}
    >
      {props.companyList && props.selectedCompanyUrl ? (
        <Select
          MenuProps={{
            hideBackdrop: true,
            PaperProps: { className: props.menuPaperClassName },
            // Without a backdrop, a click outside the menu only lands on the modal root.
            onClick: props.keyboardSupport
              ? (event: React.MouseEvent<HTMLDivElement>) => event.target === event.currentTarget && setOpen(false)
              : undefined,
            onPointerDown: props.keyboardSupport ? () => setClosedWithPointer(true) : undefined,
            onKeyDown: props.keyboardSupport ? () => setClosedWithPointer(false) : undefined,
            anchorOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
          }}
          open={open}
          SelectDisplayProps={
            props.titleOnLabel
              ? { title: props.companyList.find((company) => company.url === props.selectedCompanyUrl)?.name }
              : undefined
          }
          {...(props.keyboardSupport
            ? { onOpen: () => setOpen(true), onClose: () => setOpen(false) }
            : {
                onClick: () => {
                  open ? setOpen(false) : setOpen(true);
                },
              })}
          value={props.selectedCompanyUrl ? props.selectedCompanyUrl : ""}
          onChange={onChangeHandler}
          input={
            <Input
              classes={{
                underline: cssExports.companySelectInput,
              }}
            />
          }
          classes={{ select: cssExports.companySelect }}
          IconComponent={props.chevronIcon ? ChevronDownIcon : undefined}
        >
          {/* The cross-company overview; only for a person with the setting on and more than one company to collect. */}
          {props.showAllCompaniesOption && (props.companyList?.length ?? 0) > 1
            ? [
                <MenuItem
                  key={AllCompaniesConstants.CompanySelectorValue}
                  value={AllCompaniesConstants.CompanySelectorValue}
                  classes={{
                    root: clsx(cssExports.popoverItem, cssExports.companySelectAllItem),
                    selected: cssExports.selected,
                  }}
                >
                  <Icon icon={IconDefinitionConstants.WCompany} />
                  {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Todos.AllCompanies)}
                  <Icon icon={IconDefinitionConstants.WChevronRight} className={cssExports.companySelectAllChevron} />
                </MenuItem>,
                <Divider
                  key={`${AllCompaniesConstants.CompanySelectorValue}-divider`}
                  className={cssExports.companySelectAllDivider}
                />,
              ]
            : []}
          {props.companyList?.map((company, i) => {
            return (
              <MenuItem
                key={i}
                value={company.url}
                classes={{ root: cssExports.popoverItem, selected: cssExports.selected }}
              >
                {company.name}
              </MenuItem>
            );
          })}
        </Select>
      ) : (
        <Skeleton variant="rectangular" width={120} />
      )}
    </FormControl>
  );
};

export default CompanyList;
